import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/apiService';
import { User, LoginFormData, RegisterFormData } from '../types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginFormData) => Promise<void>;
  register: (userData: RegisterFormData) => Promise<void>;
  googleAuth: (googleData: { email: string; name: string; given_name: string; family_name: string; picture: string }) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const authCheckInProgress = useRef(false);
  const userCache = useRef<User | null>(null);
  const lastAuthCheck = useRef<number>(0);
  const AUTH_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

  const checkAuth = useCallback(async () => {
    // Prevent multiple simultaneous auth checks
    if (authCheckInProgress.current) {
      return;
    }

    // Check if we have a cached user and recent auth check
    const now = Date.now();
    if (userCache.current && (now - lastAuthCheck.current) < AUTH_CHECK_INTERVAL) {
      setUser(userCache.current);
      setIsLoading(false);
      return;
    }
    
    authCheckInProgress.current = true;
    
    try {
      if (apiService.isAuthenticated()) {
        console.log('User is authenticated, fetching profile...');
        const userProfile = await apiService.getUserProfile();
        console.log('User profile loaded:', userProfile);
        setUser(userProfile);
        userCache.current = userProfile;
        lastAuthCheck.current = now;
      } else {
        console.log('User is not authenticated');
        setUser(null);
        userCache.current = null;
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      apiService.logout();
      setUser(null);
      userCache.current = null;
    } finally {
      setIsLoading(false);
      authCheckInProgress.current = false;
      console.log('Auth check completed');
    }
  }, [AUTH_CHECK_INTERVAL]);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const refreshUser = useCallback(async () => {
    try {
      if (apiService.isAuthenticated()) {
        const userProfile = await apiService.getUserProfile();
        setUser(userProfile);
        userCache.current = userProfile;
        lastAuthCheck.current = Date.now();
      }
    } catch (err) {
      console.error('Failed to refresh user:', err);
      apiService.logout();
      setUser(null);
      userCache.current = null;
    }
  }, []);

  const login = useCallback(async (credentials: LoginFormData) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const { user: userData } = await apiService.login(credentials);
      console.log('Login successful, user data:', userData);
      setUser(userData);
      userCache.current = userData;
      lastAuthCheck.current = Date.now();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (userData: RegisterFormData) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const { user: newUser } = await apiService.register(userData);
      console.log('Registration successful, user data:', newUser);
      setUser(newUser);
      userCache.current = newUser;
      lastAuthCheck.current = Date.now();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const googleAuth = useCallback(async (googleData: { email: string; name: string; given_name: string; family_name: string; picture: string }) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const { user: newUser } = await apiService.googleAuth(googleData);
      console.log('Google auth successful, user data:', newUser);
      setUser(newUser);
      userCache.current = newUser;
      lastAuthCheck.current = Date.now();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Google authentication failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    apiService.logout();
    setUser(null);
    setError(null);
    userCache.current = null;
    lastAuthCheck.current = 0;
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextType = {
    user,
    isLoading,
    error,
    login,
    register,
    googleAuth,
    logout,
    refreshUser,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 